import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE} from 'studies/StudyConstants';
import {
  ClinicalBasedStudyExecutionOverviewQuery,
  ClinicalBasedStudyExecutionOverviewQueryVariables,
} from 'studies/typed/clinical/__generated/ClinicalBasedStudyExecutionOverviewQuery';
import {CLINICAL_BASED_STUDY_EXECUTION_OVERVIEW_QUERY} from 'studies/typed/clinical/ClinicalStudyExecutionOverview';
import {
  MultiProcedureRunStudyDesignTabContentsQuery,
  MultiProcedureRunStudyDesignTabContentsQueryVariables,
} from 'studies/typed/shared/__generated/MultiProcedureRunStudyDesignTabContentsQuery';
import {
  ProcedureRunDetailsViewQuery,
  ProcedureRunDetailsViewQueryVariables,
} from 'studies/typed/shared/__generated/ProcedureRunDetailsViewQuery';
import {MULTI_PROCEDURE_RUN_OR_PLAN_LISTING_TABLE_QUERY} from 'studies/typed/shared/MultiProcedureRunAndPlanListingTable';
import {MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY} from 'studies/typed/shared/ProcedureRunDesignTabContents';
import {PROCEDURE_RUN_DETAILS_VIEW_QUERY} from 'studies/typed/shared/ProcedureRunDetailsView';
import {ADD_PROCEDURE_RUN_BUTTON_QUERY} from 'studies/typed/utils/ui/AddProcedureRunButton';
import {LINK_TO_EXISTING_UNIT_OPERATIONS_PLAN_BUTTON_QUERY} from 'studies/typed/utils/ui/LinkToExistingUnitOperationsPlanButton';
import {STUDY_OVERVIEW_VIEW_QUERY} from 'studies/workspace/overview/StudyOverviewView';

// Refresh multi-run study overview by re-running relevant query
// TODO BNCH-127205: Add query to study adapter
export async function refreshStudyOverview(studyId: string, studyType: StudyType): Promise<void> {
  // Refetch queries that may have cached state (these don't need specific variables
  // since Apollo will refetch any active queries of these types with their original variables)
  await getApolloClient().refetchQueries({
    include: [
      MULTI_PROCEDURE_RUN_OR_PLAN_LISTING_TABLE_QUERY,
      LINK_TO_EXISTING_UNIT_OPERATIONS_PLAN_BUTTON_QUERY,
      ADD_PROCEDURE_RUN_BUTTON_QUERY,
      STUDY_OVERVIEW_VIEW_QUERY,
    ],
  });
}

// Refresh procedure run details page on multi-run studies overview
export async function refreshProcedureRunDetailsView(procedureRunId: string): Promise<void> {
  await doQuerySafe<ProcedureRunDetailsViewQuery, ProcedureRunDetailsViewQueryVariables>(
    {query: PROCEDURE_RUN_DETAILS_VIEW_QUERY},
    {
      fetchPolicy: 'network-only',
      variables: {procedureRunId},
    }
  );
}

// Refresh the design tab query so a just-saved edge returns as a derived edge. Works regardless of
// which component is mounted, unlike a refetch bound to a single hook instance.
export async function refreshProcedureRunDesign(procedureRunId: string): Promise<void> {
  await doQuerySafe<
    MultiProcedureRunStudyDesignTabContentsQuery,
    MultiProcedureRunStudyDesignTabContentsQueryVariables
  >(
    {query: MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY},
    {
      fetchPolicy: 'network-only',
      variables: {procedureRunId},
    }
  );
}

// Refresh study overview by re-running relevant query
export async function refreshClinicalBasedStudyOverview(studyId: string): Promise<void> {
  await doQuerySafe<
    ClinicalBasedStudyExecutionOverviewQuery,
    ClinicalBasedStudyExecutionOverviewQueryVariables
  >(
    {query: CLINICAL_BASED_STUDY_EXECUTION_OVERVIEW_QUERY},
    {
      fetchPolicy: 'network-only',
      variables: {studyId, numActiveProcedureRuns: ACCESSIBLE_ASSAY_RUN_COUNT_ON_OVERVIEW_PAGE + 1},
    }
  );
}
